import React from 'react';

import { FastshiftRunbookStep } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import Badge from '@atlaskit/badge';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { RunbookResponsibleMetadata } from '../runbook-card/runbook-metadata';

import { RunbookStepActionsMenu } from './runbook-step-actions-menu';
import { isRunbookStepDone } from './runbook-step-list-utils';
import { RunbookStepStatusIcons } from './runbook-step-status-icons';

const stepRowStyles = xcss({
	paddingBlock: 'space.100',
	paddingInline: 'space.100',
});

const disabledStepRowStyles = xcss({
	paddingBlock: 'space.100',
	paddingInline: 'space.100',
	backgroundColor: 'color.background.disabled',
});

const separatedStepRowStyles = xcss({
	borderBlockStart: `1px solid ${token('color.border.disabled')}`,
});

const completedStepTitleStyles = xcss({
	textDecoration: 'line-through',
	color: 'color.text.subtle',
});

const stepBadgeStyles = xcss({
	flexShrink: 0,
});

const stepTitleStyles = xcss({
	minWidth: '0',
});

export type RunbookStepRowProps = {
	step: FastshiftRunbookStep;
	hasDivider: boolean;
	runbookId: string;
	isPending: boolean;
	onMenuOpen: (step: FastshiftRunbookStep) => void;
	onToggleDisabled: (step: FastshiftRunbookStep) => void;
	onToggleRisk: (step: FastshiftRunbookStep) => void;
	onToggleStatus: (step: FastshiftRunbookStep) => void;
};

export const RunbookStepRow: React.FC<RunbookStepRowProps> = ({
	step,
	hasDivider,
	runbookId,
	isPending,
	onMenuOpen,
	onToggleDisabled,
	onToggleRisk,
	onToggleStatus,
}) => {
	return (
		<Box
			xcss={[
				step.disabled ? disabledStepRowStyles : stepRowStyles,
				hasDivider && separatedStepRowStyles,
			]}
			testId={`runbook-step-${runbookId}-${step.stepId}`}
		>
			<Stack space="space.100">
				<Inline alignBlock="start" spread="space-between" space="space.100">
					<Inline alignBlock="start" space="space.075" grow="fill">
						<Box xcss={stepBadgeStyles}>
							<Badge>{step.stepOrder}</Badge>
						</Box>
						<Box xcss={isRunbookStepDone(step) ? completedStepTitleStyles : stepTitleStyles}>
							<Text weight="medium">{step.title}</Text>
						</Box>
					</Inline>
					<Inline alignBlock="center" alignInline="end" space="space.100">
						<RunbookStepStatusIcons step={step} />
						<RunbookStepActionsMenu
							step={step}
							isPending={isPending}
							onMenuOpen={() => onMenuOpen(step)}
							onToggleDisabled={() => onToggleDisabled(step)}
							onToggleRisk={() => onToggleRisk(step)}
							onToggleStatus={() => onToggleStatus(step)}
						/>
					</Inline>
				</Inline>
				{step.responsible && <RunbookResponsibleMetadata value={step.responsible} />}
			</Stack>
		</Box>
	);
};
